/**
 * English source dictionary. `Dictionary` is derived from this file, so every
 * other locale (src/i18n/km.ts) must provide the same keys — TypeScript enforces it.
 */
const en = {
  loading: 'Loading…',
  nav: {
    aboutUs: 'About Us',
    solutions: 'Solutions',
    products: 'Products',
    features: 'Features',
    portfolios: 'Portfolios',
    contact: 'Contact',
    blogs: 'Blogs',
    docs: 'Documentations',
  },
  footer: {
    tagline:
      'AKAS provides cloud-based business ERP solutions to help enterprises streamline operations and boost productivity.',
    modules: 'Modules',
    moreModules: 'More modules',
    company: 'Company',
    contact: 'Contact',
    aboutUs: 'About Us',
    solutions: 'Solutions',
    liveDemos: 'Live demos',
    portfolios: 'Portfolios',
    blogs: 'Blogs',
    location: 'Phnom Penh, Cambodia',
    allRightsReserved: 'All rights reserved.',
    builtBy: 'Built in Phnom Penh, Cambodia · Cloud ERP for growing businesses',
  },
  hero: {
    badge: 'Cloud ERP for growing businesses',
    title: 'Run the whole business',
    titleAccent: 'on one system',
    description: 'People, sales, stock, and the books — connected, in Khmer and English.',
    primaryCta: 'Try the live demo',
    secondaryCta: 'See what it does',
  },
  process: {
    kicker: 'Connected processes',
    title: 'Enter it once. Everyone sees it.',
    lead:
      'Sales, warehouse, HR, and finance work from one shared record — no re-typing, no side spreadsheets. A won quote, a stock receipt, and a payslip all land in the same books, so your numbers finally agree.',
    openModule: 'Details',
    guide: 'Guide',
    items: [
      {
        title: 'Hire to retire',
        path: 'Requisition → Hire → Attend / leave → Pay → Review → Exit',
        description:
          'Recruitment, personnel, attendance, leave, payroll (including tax, NSSF, and seniority), performance, training, and self-service sit on one employee record.',
      },
      {
        title: 'Lead to cash',
        path: 'Lead → Customer → Opportunity → Quote → Order → Invoice → Receipt',
        description:
          'CRM quotations convert to sales orders. Invoices, KHQR, deposits, and AR live in Revenues. POS and the online store sell the same items.',
      },
      {
        title: 'Procure to pay',
        path: 'Vendor → Purchase order → Receive → Bill → Payment',
        description:
          'Inventory orders and receipts share vendors with Expenses. Bills, deposits, and AP query post to the ledger when Accounting mappings are set.',
      },
    ],
  },
  modules: {
    kicker: 'ERP modules',
    title: 'What you get with AKAS',
    lead:
      'Start with what you need today — people, selling, buying, stock, the books, the till, or a web shop. Everything shares the same customers, items, locations, and approvals, so adding a module later just works.',
    moduleDetails: 'Details',
    userGuide: 'Guide',
    browse: {
      kicker: 'All features',
      title: 'See how every module connects',
      description:
        'Payroll, sales, stock, and the books — with the detail behind each claim and a guide for every area.',
      link: 'Explore features →',
    },
  },
  platform: {
    kicker: 'Easy to run, hard to misuse',
    title: 'One place to set your rules — every module follows',
    description:
      'Your org structure, approval rules, languages, and document formats are set once in administration, and each department inherits them. That is why a clinic till, a payslip, and a journal entry can live in one system — and why onboarding a new branch does not mean a new project.',
    platformLink: 'Platform details',
    adminGuide: 'Admin user guide',
    items: [
      {
        title: 'Set your own rules',
        body: 'Draw the approval path for leave, quotes, bills, and stock. Change it as the business changes — no vendor, no new build.',
      },
      {
        title: 'Trust who sees what',
        body: 'Every role gets its own menus, and sensitive steps like payroll stay behind extra sign-in protection.',
      },
      {
        title: 'Everyone works in their language',
        body: 'Khmer and English everywhere — screens, documents, payslips. More languages whenever you need them.',
      },
      {
        title: 'Make the system fit you',
        body: 'Add the fields and rearrange the screens your industry needs with a point-and-click editor.',
      },
      {
        title: 'One company structure',
        body: 'Branches, departments, warehouses, currencies, and the fiscal calendar set once — every module obeys.',
      },
      {
        title: 'Ask instead of waiting for reports',
        body: 'An optional assistant that answers questions about your own business data — over the AI service you choose, or run locally.',
      },
      {
        title: 'Local payments and payroll',
        body: 'Scan-to-pay (KHQR) invoices, salary tax, NSSF, and seniority reports are part of the product — not extra spreadsheets.',
      },
      {
        title: 'Ready for anyone who checks',
        body: 'Automatic document numbering and a change history on sensitive records — the auditor sits down and nods.',
      },
    ],
  },
  features: {
    badge: 'Why AKAS',
    title: 'Built as an ERP, not a pile of apps',
    description:
      'Custom fields, translations, and workflows absorb most “we work differently” requests. Watch the designer, then read the module guides on docs.akas.co.',
    items: [
      {
        title: 'Payday you stop worrying about',
        description:
          'Salary tax, NSSF, seniority, and bank transfer reports come out of the payroll run already prepared — your finance team reviews instead of compiling, and staff get payslips in English or Khmer on their phone.',
      },
      {
        title: 'Decisions that don’t sit on a desk',
        description:
          'Leave, purchases, quotes, and stock requests route to the right approver automatically. Staff self-serve what used to be paper, and managers approve from their phone — with a record behind every yes.',
      },
      {
        title: 'One honest set of numbers',
        description:
          'A customer, item, or vendor is entered once and used by sales, stock, the till, and the books alike. Cash comes in sooner with payable invoices, and stock stops living on a spreadsheet.',
      },
      {
        title: 'Control without micromanaging',
        description:
          'Each role sees only its own world. Sensitive changes are recorded, documents follow your numbering, and approving hands are never the bookkeeping hands.',
      },
    ],
    videos: [
      {
        title: 'Customizable systems',
        description: 'Form layout and customization demo',
      },
      {
        title: 'Approval workflow',
        description: 'Design approval workflow in AKAS.',
      },
    ],
    detailLink: 'See what AKAS does for your business →',
    detailNote:
      'Payroll that files itself, invoices customers pay faster, stock you can trust, and approvals that move.',
  },
  testimonials: {
    badge: 'Our Testimonials',
    title: 'What Our Clients Say',
    items: [
      {
        content:
          'AKAS has transformed the way we manage our business operations with its comprehensive cloud-based modules, streamlining everything from CRM to accounting seamlessly.',
        author: 'Chan Sombath',
        position: 'CEO, OWP',
      },
      {
        content:
          'AKAS has transformed our operations with its comprehensive cloud-based HR system, streamlining our HR processes and significantly boosting our efficiency.',
        author: 'Hour Chansokun',
        position: 'Administrative Director, iDE Cambodia',
      },
      {
        content:
          'AKAS has revolutionized our HR operations through its all-in-one cloud-based HR platform, simplifying our HR workflows and greatly enhancing our overall productivity.',
        author: 'Tham Thavy',
        position: 'Head of HR, ICF Cambodia',
      },
    ],
  },
  notFound: {
    title: 'Page not found | AKAS',
    description: 'The page you requested could not be found on AKAS.',
    heading: 'This page isn’t available',
    body: 'The link may be broken, or the page may have been removed.',
    backHome: 'Back to home',
  },
};

export default en;
export type Dictionary = typeof en;
